import React, { useState, useEffect, useMemo } from 'react';
import ObjectForm from '../../Common/ObjectForm';
import http from '../../Common/RestAPIHandler';
import countryCodes from '../../Common/CountryCodes';
import { userManagementForm } from '../../Common/Languages';

const UserForm = (props) => {
  const [formFields, setFormFields] = useState();
  const [allRoles, setAllRoles] = useState({});
  const [roleMappings, setRoleMappings] = useState([]);
  const [availableRoles, setAvailableRoles] = useState({});

  const rawLang = localStorage.getItem('languages');
  const lang =
    rawLang !== null &&
    !isNaN(rawLang) &&
    parseInt(rawLang) >= 0 &&
    parseInt(rawLang) <= 2
      ? parseInt(rawLang)
      : 0;

  // Memoize country code map
  const countryCodeMap = useMemo(() => {
    const map = {};
    Object.keys(countryCodes).forEach(function (c) {
      if (!map[countryCodes[c].code]) {
        map[countryCodes[c].code] = countryCodes[c].name;
      } else {
        map[countryCodes[c].code] =
          map[countryCodes[c].code] + ' / ' + countryCodes[c].name;
      }
    });

    Object.entries(map).forEach(([key, value]) => {
      map[key] = value + ' (' + key + ')';
    });

    return map;
  }, []);

  const defaultObject = useMemo(
    () => ({
      tenant: props.user?.tenant,
      is_tenant_admin: false,
      is_readonly: false,
      has_access: true,
      number_of_result_per_page: 25,
    }),
    [props.user?.tenant]
  );

  const getRoleMappings = () => {
    if (props.objectId) {
      http.Get(
        `user_roles?user_id=${props.objectId}`,
        (data) => {
          var roleList = [];
          for (let i = 0; i < data.length; i++) {
            data[i]['value'] = data[i]['role_id'];
            data[i]['label'] = allRoles[data[i]['role_id']];
            data[i]['onDelete'] = (e, object, setObject, setErrors) => {
              http.Delete(
                'user_roles/' + data[i]['id'],
                {},
                (data) => {
                  getRoleMappings();
                },
                (status, error) => {
                  setErrors(error);
                }
              );
            };
            roleList.push(data[i]);
          }
          setRoleMappings(roleList);
        },
        () => {}
      );
    } else {
      var data = localStorage.getItem('rolesForTenantUser');
      if (data) {
        var roles = data.split(',');
        var roleList = [];
        roles.map((role) => {
          if (role) {
            var roleData = {};
            roleData['value'] = role;
            roleData['label'] = allRoles[role];
            roleData['onDelete'] = (e, object, setObject, setErrors) => {
              var roles = localStorage.getItem('rolesForTenantUser');
              if (roles) {
                localStorage.setItem(
                  'rolesForTenantUser',
                  roles.replace(role + ',', '')
                );
                getRoleMappings();
              }
              var subDatas = object.subDatas;
              if (subDatas) {
                var newSubDatas = subDatas.filter((subData) => {
                  return subData.data.role_id !== role;
                });
                setObject({
                  ...object,
                  subDatas: newSubDatas,
                });
              }
            };
            roleList.push(roleData);
          }
        });
        setRoleMappings(roleList);
      } else {
        localStorage.setItem('rolesForTenantUser', '');
        setRoleMappings([]);
      }
    }
  };

  // Memoize the form fields configuration
  const memoizedFormFields = useMemo(() => {
    if (Object.keys(allRoles).length === 0) {
      return null;
    }

    return [
      {
        id: 'first_name',
        label: userManagementForm.FIRSTNAME?.[lang] || 'First Name',
        type: 'text',
      },
      {
        id: 'last_name',
        label: userManagementForm.LASTNAME?.[lang] || 'Last Name',
        type: 'text',
      },
      {
        id: 'email',
        label: userManagementForm.EMAIL?.[lang] || 'Email',
        type: 'email',
      },
      {
        id: 'profile_image',
        label: userManagementForm.PROFILEIMAGE?.[lang] || 'Profile Image',
        type: 'image',
        onChange: (e, object, setObject, setErrors) => {
          if (e.target.files && e.target.files.length === 1) {
            var formData = new FormData();
            formData.append(
              'profile_image',
              e.target.files[0],
              e.target.files[0].name
            );
            formData.append('tenant_user_id', object.id);
            http.Post(
              'tenant_user_logo',
              formData,
              (data) => {
                setObject(data);
                if (props.fetchData) props.fetchData();
              },
              (status, error) => {
                setErrors(error);
              }
            );
          }
        },
        onDelete: (e, object, setObject, setErrors) => {
          object['profile_image'] = null;
          http.Put(
            'tenant_users/' + object.id,
            object,
            (data) => {
              setObject(data);
              if (props.fetchData) props.fetchData();
            },
            (status, error) => {
              setErrors(error);
            }
          );
        },
      },
      {
        id: 'department',
        label: userManagementForm.DEPARTMENT?.[lang] || 'Department',
        type: 'text',
      },
      {
        id: 'phone',
        label: userManagementForm.PHONE?.[lang] || 'Phone',
        type: 'text',
      },
      {
        id: 'ext',
        label: userManagementForm.EXT?.[lang] || 'Ext',
        type: 'text',
      },
      {
        id: 'mobile',
        label: userManagementForm.MOBILE?.[lang] || 'Mobile',
        type: 'text',
      },
      {
        id: 'country_code',
        label: userManagementForm.COUNTRYCODE?.[lang] || 'Country Code',
        type: 'select',
        valueMap: countryCodeMap,
      },
      {
        id: 'roles',
        label: userManagementForm.ROLES?.[lang] || 'Roles',
        type: 'multiselect',
        valueMap: availableRoles,
        selectedValues: roleMappings,
        onChange: (e, object, setObject, setErrors) => {
          if (object.id) {
            http.Post(
              'user_roles',
              { user_id: object.id, role_id: e.target.value },
              (data) => {
                getRoleMappings();
              },
              (status, error) => {
                setErrors(error);
              }
            );
          } else {
            var subDatas = object.subDatas;
            if (!subDatas) {
              subDatas = [];
            }
            subDatas.push({
              url: 'user_roles',
              data: { role_id: e.target.value },
              id: 'user_id',
            });
            setObject({
              ...object,
              subDatas: subDatas,
            });

            var data = localStorage.getItem('rolesForTenantUser');
            if (!data) {
              data = '';
            }
            localStorage.setItem(
              'rolesForTenantUser',
              data + e.target.value + ','
            );
            getRoleMappings();
          }
        },
      },
      {
        id: 'is_readonly',
        label: userManagementForm.LICENSETYPE?.[lang] || 'License Type',
        type: 'select',
        valueMap: {
          false: `Standard License ( ${props.tenant ? props.tenant.user_addons - props.tenant.user_count : 0} of ${props.tenant ? props.tenant.user_addons : 0} available)`,
          true: `Read Only ( ${props.tenant ? props.tenant.readonly_addons - props.tenant.readonly_count : 0} of ${props.tenant ? props.tenant.readonly_addons : 0} available)`,
        },
      },
      {
        id: 'is_tenant_admin',
        label: userManagementForm.ISTENANTADMIN?.[lang] || 'Is Tenant Admin',
        type: 'radio',
      },
      {
        id: 'has_access',
        label: userManagementForm.HASACCESS?.[lang] || 'Has Access',
        type: 'radio',
      },
      {
        id: 'password_need_change',
        label: userManagementForm.REQUIRE?.[lang] || 'Require Password Change',
        type: 'radio',
      },
      ...(props.objectId
        ? [
            // EDIT MODE: Show status + resend option
            {
              id: 'send_welcome_email_status',
              label: 'WELCOME EMAIL PREVIOUSLY SENT',
              type: 'display',
              getValue: (object) =>
                object.send_welcome_email ? '✓ Yes' : '✗ No',
            },
            {
              id: 'resend_welcome_email',
              label: 'RE-SEND WELCOME EMAIL',
              type: 'radio',
            },
          ]
        : [
            // CREATE MODE: Show original send welcome email option
            {
              id: 'send_welcome_email',
              label: 'SEND WELCOME EMAIL',
              type: 'radio',
            },
          ]),
      {
        id: 'is_hidden',
        label:
          userManagementForm.CONTACTLIST?.[lang] || 'Hide from Contact List',
        type: 'radio',
      },
      {
        id: 'is_mobile_hidden',
        label:
          userManagementForm.HIDECONTACTLIST?.[lang] ||
          'Hide Mobile from Contact List',
        type: 'radio',
      },
      {
        id: 'number_of_result_per_page',
        label: userManagementForm.NUMBEROFRESULT?.[lang] || 'Results Per Page',
        type: 'select',
        valueMap: { 25: 25, 50: 50, 100: 100, 200: 200 },
      },
    ];
  }, [
    allRoles,
    availableRoles,
    roleMappings,
    countryCodeMap,
    lang,
    props.tenant,
  ]);

  // Fetch roles only once on mount
  useEffect(() => {
    http.Get(
      `roles`,
      (data) => {
        const valueMap = {};
        for (let i = 0; i < data.length; i++) {
          valueMap[data[i].id] = data[i].role_name;
        }
        setAllRoles(valueMap);
      },
      () => {
        console.error('Failed to fetch roles');
      }
    );
    localStorage.setItem('rolesForTenantUser', '');
  }, []);

  useEffect(() => {
    if (Object.keys(allRoles).length > 0) {
      getRoleMappings();
    }
  }, [allRoles]);

  useEffect(() => {
    if (Object.keys(allRoles).length > 0 && roleMappings.length >= 0) {
      const availableList = { ...allRoles };
      for (let i = 0; i < roleMappings.length; i++) {
        if (availableList[roleMappings[i]['value']]) {
          delete availableList[roleMappings[i]['value']];
        }
      }
      setAvailableRoles(availableList);
    }
  }, [allRoles, roleMappings]);

  // Update form fields only when memoized fields change
  useEffect(() => {
    console.log('=== FORM FIELDS DEBUG ===');
    console.log('props.object:', props.object);
    console.log('props.object?.id:', props.object?.id);
    console.log('Type of props.object:', typeof props.object);
    console.log('Conditional result:', !!(props.object && props.object.id));
    console.log('========================');
    setFormFields(memoizedFormFields);
  }, [memoizedFormFields]);

  return (
    <div>
      {formFields && (
        <ObjectForm
          {...props}
          objectId={props.objectId}
          url={`tenant_users`}
          formName={userManagementForm.TenantUsers?.[lang] || 'Tenant Users'}
          formFields={formFields}
          object={defaultObject}
        />
      )}
    </div>
  );
};

export default UserForm;
